import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseData, useDatabaseItem, usePage } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Combobox } from "@/components/ui/combobox"
import { zodResolver } from "@hookform/resolvers/zod"
import { LayoutList } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { supabase } from "@/lib/data"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"

const FormSchema = z.object({
  name: z.string(),
  description: z.string(),
  category: z.coerce.number(),
  key_signals: z.string(),
  type: z.enum(["user_message", "agent_response"]),
  weight: z.coerce.number().optional(),
  askhuman: z.boolean().nullable().optional(),
  highrisk: z.boolean().nullable().optional(),
  lowconf: z.boolean().nullable().optional(),
})

type FormData = z.infer<typeof FormSchema>
type EvalItem = Table<"eval_items">


export function Evalitems_CRUD() {

  const { team } = usePage()
  const { data, loading, submit, toastSuccess, navigate } = useDatabaseItem("eval_items")
  const { data: categories, loading: categoriesLoading } = useDatabaseData("eval_categories", { fields: ["id", "name"], filter: q => q.eq("team", team?.id) })

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data as FormData,
    values: data as FormData,
  })

  async function onSubmit(data: EvalItem) {
    data.team = team?.id
    await submit(data)
    toastSuccess()
    navigate("/evaluations/evalitems")
  }

  if (loading || categoriesLoading) return <CRUDLoader />

  const categoryOptions = categories?.map((c: any) => ({ value: c.id, label: c.name })) || []

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit as any)}>
          <PageTitle>
            <CRUDToolbar table="eval_items" onSuccess="/evaluations/evalitems" />
          </PageTitle>
          <div className="rgrid">
            <div className="row">
              <div className="rc-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-2">
                <FormField control={form.control} name="category" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Combobox items={categoryOptions} value={field.value} onChange={field.onChange} placeholder="Search category..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-4">
                <FormField control={form.control} name="type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Applies To</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select scope" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="user_message">User</SelectItem>
                        <SelectItem value="agent_response">Agent</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-2">
                <FormField control={form.control} name="weight" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Weight</FormLabel>
                    <FormControl><Input type="number" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Textarea {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="key_signals" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key Signals</FormLabel>
                    <FormControl><Textarea {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12 flex gap-6 items-center pt-2">
                <FormField control={form.control} name="askhuman" render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value ?? false} onCheckedChange={(v) => field.onChange(v === true ? true : v === false ? false : null)} />
                    </FormControl>
                    <FormLabel className="font-normal">Ask Human</FormLabel>
                  </FormItem>
                )} />
                <FormField control={form.control} name="highrisk" render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value ?? false} onCheckedChange={(v) => field.onChange(v === true ? true : v === false ? false : null)} />
                    </FormControl>
                    <FormLabel className="font-normal">High Risk</FormLabel>
                  </FormItem>
                )} />
                <FormField control={form.control} name="lowconf" render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value ?? false} onCheckedChange={(v) => field.onChange(v === true ? true : v === false ? false : null)} />
                    </FormControl>
                    <FormLabel className="font-normal">Low Confidence</FormLabel>
                  </FormItem>
                )} />
              </div>

            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>

}
